module Client.Components.Graph.WaterQualitySensor

open Client.Components.Graph.Recharts
open Client.Components.GraphCommon
open Client.Domain.MeasuredValueString
open Feliz.Recharts
open Fulma
open Shared.Dto.MapSensorData
open Shared.Dto.SensorGraphData
open Shared.Infrastructure

let private xAxisTickMargin = 8
let private yAxisTickWidth = 90

type WaterQualityGraphNode = {
    Ticks: double

    Turbidity: float option
    DissolvedOxygen: float option
    OxidationReductionPotential: int option
    EC: int option
    PH: float option
}

let toWaterQualitySensorGraphData (data: GraphWaterQualityDataDto) : WaterQualityGraphNode = {
    Ticks = data.TimeStamp.ToUnixTimeSeconds() |> double

    Turbidity = data.Turbidity
    DissolvedOxygen = data.DissolvedOxygen
    OxidationReductionPotential = data.OxidationReductionPotential
    EC = data.EC
    PH = data.PH
}

let mapDataToWaterQuality (data: GraphDataDto) =
    match data with
    | WaterQuality data -> Some data
    | _ -> None

let currentWaterQualityDataBox (data: WaterQualityData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        currentDataLevelItem "Leitfähigkeit" (data.EC |> Option.map conductivityToString |> Option.defaultValue "-")
        currentDataLevelItem "PH-Wert" (data.PH |> Option.map phToString |> Option.defaultValue "-")
    ]
]

let private createEcYAxisTicks (data: WaterQualityGraphNode list) =
    let values = data |> List.choose (fun item -> item.EC) |> List.map float

    let min = List.min values
    let max = List.max values

    AxisTicks.generate true 1. min max

let private yAxisTickFormatter (unit: string) (factor: float) (value: float) = sprintf "%.2f %s" (value / factor) unit

let private createPhYAxisTicks (data: WaterQualityGraphNode list) =
    let values = data |> List.choose (fun item -> item.PH)

    let min = List.min values
    let max = List.max values

    AxisTicks.generate true 1. min max

let private phGraph (graphData: SimpleGraphData<WaterQualityGraphNode>) =
    let xDomain = xAxis.domain (domain.min, domain.max)
    let yDomain = yAxis.domain (domain.min, domain.max)
    let xAxisTickInterval = xAxis.interval.preserveStartEnd

    let phYAxisTicks = graphData.Data |> createPhYAxisTicks |> AxisTicks.createYProperty

    let dataKey = (fun (p: WaterQualityGraphNode) -> p.Ticks) |> xAxis.dataKey

    Recharts.lineChart [
        lineChart.syncId "syncId1"
        lineChart.data graphData.Data
        lineChart.children [
            Recharts.xAxis [
                dataKey
                xAxis.number
                xAxis.scale.time
                xAxis.tickFormatter formatTimeTick
                xDomain
                xAxisTickInterval
                xAxis.tickMargin xAxisTickMargin
            ]
            Recharts.yAxis [
                yAxis.width yAxisTickWidth
                yAxis.number
                phYAxisTicks
                yDomain
            ]
            Recharts.legend []
            Recharts.tooltip [ tooltip.content tooltipContent ]
            Recharts.line [
                line.dot false
                line.dataKey (fun p -> p.PH)
                line.name "PH-Wert"
                line.stroke "#cc9933"
            ]

            Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
        ]
    ]
    |> fullHeightGraphBox "Verlauf des PH-Werts"

let private ecGraph (graphData: SimpleGraphData<WaterQualityGraphNode>) =
    let xDomain = xAxis.domain (domain.min, domain.max)
    let yDomain = yAxis.domain (domain.min, domain.max)
    let xAxisTickInterval = xAxis.interval.preserveStartEnd

    let yAxisTicks = graphData.Data |> createEcYAxisTicks

    let ecYAxisTicks = AxisTicks.createYProperty yAxisTicks

    let dataKey = (fun (p: WaterQualityGraphNode) -> p.Ticks) |> xAxis.dataKey

    Recharts.lineChart [
        lineChart.syncId "syncId1"
        lineChart.data graphData.Data
        lineChart.children [
            Recharts.xAxis [
                dataKey
                xAxis.number
                xAxis.scale.time
                xAxis.tickFormatter formatTimeTick
                xDomain
                xAxisTickInterval
                xAxis.tickMargin xAxisTickMargin
            ]
            Recharts.yAxis [
                yAxis.width yAxisTickWidth
                yAxis.number
                ecYAxisTicks
                yDomain
                yAxis.unit " μS/cm"
            ]
            Recharts.legend []
            Recharts.tooltip [ tooltip.content tooltipContent ]
            Recharts.line [
                line.dot false
                line.dataKey (fun p -> p.EC)
                line.name "Leitfähigkeit [μS/cm]"
                line.stroke "#cc9933"
            ]

            Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
        ]
    ]
    |> fullHeightGraphBox "Verlauf des Leitfähigkeit"

let waterQualityGraphs (graphData: SimpleGraphData<WaterQualityGraphNode>) = [ ecGraph graphData; phGraph graphData ]

let graphDataToWaterQualityGraphData (data: GraphWaterQualityDataDto list) =
    data |> List.map toWaterQualitySensorGraphData |> (fun list -> { Data = list })